import {
  CreateRecurringInvoiceInput,
  RecurringInvoice,
  RecurringInvoiceStatus,
  RecurringInvoiceWithInstances,
  UpdateRecurringInvoiceInput,
} from "@/features/invoices/types/recurring";
import api from "@/lib/axios";

// Centralized query keys for recurring invoices domain
export const recurringInvoiceKeys = {
  all: ["recurring-invoices"] as const,
  lists: () => [...recurringInvoiceKeys.all, "list"] as const,
  list: (status?: RecurringInvoiceStatus | RecurringInvoiceStatus[]) =>
    [...recurringInvoiceKeys.lists(), { status }] as const,
  details: () => [...recurringInvoiceKeys.all, "detail"] as const,
  detail: (id: string) => [...recurringInvoiceKeys.details(), id] as const,
};

export const recurringInvoicesApi = {
  /**
   * Get all recurring invoices, optionally filtered by status
   * GET /invoices/recurring?status=active,canceled
   */
  getAll: async (
    status?: RecurringInvoiceStatus | RecurringInvoiceStatus[],
  ): Promise<RecurringInvoice[]> => {
    const params: Record<string, string> = {};
    if (status) {
      params.status = Array.isArray(status) ? status.join(",") : status;
    }
    const response = await api.get<RecurringInvoice[]>("/invoices/recurring", {
      params,
    });
    return response.data;
  },

  /**
   * Get a single recurring invoice by ID with issued invoices
   * GET /invoices/recurring/:id
   */
  getById: async (id: string): Promise<RecurringInvoiceWithInstances> => {
    const response = await api.get<any>(`/invoices/recurring/${id}`);
    const data = response.data;

    // Normalize the response to handle different backend field formats
    return {
      id: data.id || data._id,
      type: data.type,
      customerId: data.customerId,
      customerName: data.customerName,
      description: data.description || "",
      currency: data.currency || "usd",
      amount: data.amount || 0,
      frequency: data.frequency || 1,
      frequencyUnit: data.frequencyUnit || "months",
      status: data.status,
      invoicesSent: data.invoicesSent ?? data.instancesGenerated ?? 0,
      dueDateType: data.dueDateType || "immediate",
      dueDateDays: data.dueDateDays || 0,
      endCondition: data.endCondition || "never",
      endAfterCount: data.endAfterCount ?? null,
      createdAt: data.createdAt || 0,
      canceledAt: data.canceledAt ?? data.cancelledAt ?? null,
      completedAt: data.completedAt ?? null,
      nextInvoiceAt: data.nextInvoiceAt ?? data.nextInvoiceDate ?? null,
      issuedInvoices: (data.issuedInvoices || []).map((inv: any) => ({
        id: inv.id,
        number: inv.number || "",
        date: inv.date || inv.created || 0,
        amount: inv.amount || 0,
        currency: inv.currency || "usd",
        status: inv.status || "draft",
        pdfUrl: inv.pdfUrl || inv.pdf_url || null,
        hostedInvoiceUrl:
          inv.hostedInvoiceUrl || inv.hosted_invoice_url || null,
      })),
      issuedInvoicesCount:
        data.issuedInvoicesCount ?? data.issuedInvoices?.length ?? 0,
    };
  },

  /**
   * Create a new recurring invoice
   * POST /invoices/recurring
   */
  create: async (
    data: CreateRecurringInvoiceInput,
  ): Promise<RecurringInvoice> => {
    const response = await api.post<RecurringInvoice>(
      "/invoices/recurring",
      data,
    );
    return response.data;
  },

  /**
   * Update a recurring invoice (description, payment due)
   * PATCH /invoices/recurring/:id
   */
  update: async (
    id: string,
    data: UpdateRecurringInvoiceInput,
  ): Promise<RecurringInvoice> => {
    const response = await api.patch<RecurringInvoice>(
      `/invoices/recurring/${id}`,
      data,
    );
    return response.data;
  },

  /**
   * Cancel a recurring invoice
   * POST /invoices/recurring/:id/cancel
   */
  cancel: async (id: string): Promise<void> => {
    await api.post(`/invoices/recurring/${id}/cancel`);
  },
};
